import { useEffect, useState } from 'react';

interface HeroData {
  eyebrow: string;
  headline: string;
  headlineAccent: string;
  subheadline: string;
  primaryCTA: { label: string; href: string; external?: boolean };
  secondaryCTA?: { label: string; href: string; external?: boolean };
  backgroundImage: string;
}

interface HeroSectionProps {
  data: HeroData;
}

const STATS = [
  { value: '500+', label: 'Installs Completed' },
  { value: '2015', label: 'Year Founded' },
  { value: '2', label: 'States Licensed' },
  { value: '30%', label: 'Federal Tax Credit' },
];

const HeroSection = ({ data }: HeroSectionProps) => {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setLoaded(true), 100);
    return () => clearTimeout(timer);
  }, []);

  return (
    <section className="relative min-h-screen flex flex-col justify-end overflow-hidden">
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src={data.backgroundImage}
          alt="Premium solar installation on luxury home"
          className="w-full h-full object-cover object-center"
        />
        {/* Multi-layer cinematic overlays */}
        <div className="absolute inset-0 bg-gradient-to-r from-zinc-950/95 via-zinc-950/75 to-zinc-950/25" />
        <div className="absolute inset-0 bg-gradient-to-t from-zinc-950/80 via-zinc-950/20 to-transparent" />
        <div className="absolute inset-0 bg-zinc-950/20" />
      </div>

      {/* Ambient glow */}
      <div className="absolute top-1/3 left-0 w-[600px] h-[600px] bg-amber-500/8 rounded-full blur-3xl pointer-events-none" />

      {/* Main content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 w-full pb-0 pt-32">
        {/* Eyebrow */}
        <div
          className={`flex items-center gap-3 mb-8 transition-all duration-1000 ${
            loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}
        >
          <div className="w-10 h-px bg-amber-400" />
          <span className="text-amber-400 text-xs font-semibold tracking-[0.25em] uppercase">
            {data.eyebrow}
          </span>
        </div>

        {/* Headline */}
        <h1
          className={`font-serif text-6xl md:text-7xl lg:text-8xl xl:text-[96px] text-white leading-[0.95] mb-8 max-w-4xl transition-all duration-1000 delay-100 ${
            loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          {data.headline}{' '}
          <em className="text-amber-400 not-italic block">{data.headlineAccent}</em>
        </h1>

        {/* Subheadline */}
        <p
          className={`text-zinc-300 text-xl md:text-2xl leading-relaxed mb-12 max-w-xl font-light transition-all duration-1000 delay-200 ${
            loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          {data.subheadline}
        </p>

        {/* CTAs */}
        <div
          className={`flex flex-wrap gap-4 mb-16 transition-all duration-1000 delay-300 ${
            loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <a
            href={data.primaryCTA.href}
            target={data.primaryCTA.external ? '_blank' : undefined}
            rel={data.primaryCTA.external ? 'noopener noreferrer' : undefined}
            className="inline-flex items-center gap-3 bg-amber-500 hover:bg-amber-400 text-zinc-950 font-bold text-sm px-9 py-5 rounded-full whitespace-nowrap transition-all duration-300 cursor-pointer tracking-wide"
          >
            {data.primaryCTA.label}
            <div className="w-5 h-5 flex items-center justify-center bg-zinc-950/20 rounded-full">
              <i className="ri-arrow-right-line text-xs" />
            </div>
          </a>
          {data.secondaryCTA && (
            <a
              href={data.secondaryCTA.href}
              target={data.secondaryCTA.external ? '_blank' : undefined}
              rel={data.secondaryCTA.external ? 'noopener noreferrer' : undefined}
              className="inline-flex items-center gap-3 border-2 border-white/50 hover:border-amber-400 hover:text-amber-400 text-white font-semibold text-sm px-9 py-5 rounded-full whitespace-nowrap transition-all duration-300 backdrop-blur-sm cursor-pointer tracking-wide"
            >
              {data.secondaryCTA.label}
            </a>
          )}
        </div>

        {/* Trust chips */}
        <div
          className={`flex flex-wrap gap-3 mb-0 pb-0 transition-all duration-1000 delay-[400ms] ${
            loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}
        >
          {[
            { icon: 'ri-shield-check-fill', text: 'Licensed & Insured — IN & KY' },
            { icon: 'ri-home-gear-line', text: 'Family Owned Since 2015' },
            { icon: 'ri-percent-line', text: '30% Federal Tax Credit Available' },
            { icon: 'ri-map-pin-line', text: 'Serving Kentuckiana' },
          ].map((chip) => (
            <div
              key={chip.text}
              className="flex items-center gap-2.5 bg-white/10 backdrop-blur-md border border-white/15 rounded-full px-5 py-2.5"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className={`${chip.icon} text-amber-400 text-sm`} />
              </div>
              <span className="text-white/90 text-xs font-medium whitespace-nowrap">{chip.text}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Stats bar — floated at bottom */}
      <div
        className={`relative z-10 w-full mt-16 transition-all duration-1000 delay-500 ${
          loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}
      >
        <div className="bg-white/8 backdrop-blur-xl border-t border-white/10">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-2 md:grid-cols-4">
              {STATS.map((stat, i) => (
                <div
                  key={stat.label}
                  className={`py-7 text-center ${
                    i < STATS.length - 1 ? 'border-r border-white/10' : ''
                  }`}
                >
                  <p className="font-serif text-4xl text-amber-400 font-bold leading-none mb-1.5">
                    {stat.value}
                  </p>
                  <p className="text-white/60 text-xs tracking-wider uppercase">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div
        className={`absolute bottom-40 right-8 flex flex-col items-center gap-2 transition-all duration-1000 delay-700 ${
          loaded ? 'opacity-100' : 'opacity-0'
        }`}
      >
        <div className="w-px h-12 bg-gradient-to-b from-transparent via-white/30 to-white/60 animate-pulse" />
        <span className="text-white/40 text-[10px] tracking-[0.2em] uppercase rotate-90 translate-y-6">
          Scroll
        </span>
      </div>
    </section>
  );
};

export default HeroSection;
